<template>
  <div class="board-view-modal-style">
    <PreviewSpace
      v-if="!inEpicBoard"
      v-show="$store.getters.displayProjectPreviewBoards"
    />
    <ArchivedBar
      v-if="boardArchived"
      :type="'boardArchived'"
      @restore="unarchiveBoard"
    />
    <DeletedProjectBanner
      v-if="socketDeletedProject"
      :resource-type="'board'"
      :deleted-project-name="currentProjectTitle"
    />
    <BoardGridView
      :class="[`cards-layout--${currentCardsLayout}`, { 'in-epic-board': inEpicBoard }]"
      :layout="currentCardsLayout"
    />
  </div>
</template>

<script>
import BoardGridView from '@/components/pages/BoardGridView';
import ArchivedBar from '@/components/widgets/ArchivedBar';
import PreviewSpace from '@/components/sections/PreviewSpace.vue';
import socketWatcher from '@/mixins/SocketWatcher';
import DeletedProjectBanner from '@/components/elements/DeletedProjectBanner.vue';
import useBoardEmbed from '@/boards/composables/useBoardEmbed';
import headerMixin from '@/mixins/headerMixin';
import usePageWindowMeta from '@/utilities/composables/usePageWindowMeta';
import { BoardLayout } from '@superthread-com/api';
import {
  browserStorageGetItem,
  browserStorageGetAndSetItem,
} from '@/utilities/browser-storage.util';
import { localBoardLayoutKey } from '@/utilities/localDrafts';
import useTimelineLayoutFeature from '@/utilities/composables/useTimelineLayoutFeature';
import useCalendarLayoutFeature from '@/utilities/composables/useCalendarLayoutFeature';

export default {
  name: 'BoardView',
  components: {
    BoardGridView,
    ArchivedBar,
    PreviewSpace,
    DeletedProjectBanner,
  },

  mixins: [socketWatcher, headerMixin],

  props: {
    cardsLayout: {
      type: String,
      default: '',
    },
  },

  setup() {
    const { embeddedBoardAttributes, embed, inEpicBoard } = useBoardEmbed();
    const { windowMeta } = usePageWindowMeta();
    const { timelineLayoutFeatureAvailable } = useTimelineLayoutFeature();
    const { calendarLayoutFeatureAvailable } = useCalendarLayoutFeature();

    return {
      embeddedBoardAttributes,
      embed,
      inEpicBoard,
      windowMeta,
      timelineLayoutFeatureAvailable,
      calendarLayoutFeatureAvailable,
    };
  },

  data() {
    return {
      boardIdWatcher: null,
    };
  },

  head() {
    // disable this so we dont overwrite meta for epics (added from BoardListCardDetails)
    if (this.inEpicBoard) return '';

    if (this.embed && this.isPage) {
      return this.windowMeta;
    }

    const metaBoardTitle =
      this.currentBoardId === this.$constants.epicsBoardId
        ? this.translate('roadmap')
        : this.currentBoard.title || '';

    if (!this.currentProjectTitlte) {
      return {
        title: metaBoardTitle,
      };
    }

    return {
      title: `${this.currentProjectTitle} | ${metaBoardTitle}`,
    };
  },

  computed: {
    isProjectDeleted() {
      return this.getTrashProjects?.some((project) => project.id === this.currentBoardProjectId);
    },
    socketDeletedProject() {
      return this.$store.getters.socketDeletedProjects.includes(this.currentBoardProjectId);
    },
    getTrashProjects() {
      return this.$store.getters.getTrashProjects || [];
    },
    displayInfoSidebar() {
      return this.$store.getters.displayInfoSidebar;
    },
    currentBoard() {
      return this.$store.getters.getFlatBoards[this.currentBoardId] || {};
    },
    boardCreator() {
      return this.$store.getters.getMemberById(this.currentBoard.user_id_posted);
    },
    currentProjectTitle() {
      const project = this.$store.getters.getProject(this.currentBoard.project_id);
      return project?.title || '';
    },
    boardArchived() {
      return this.currentBoard.archived;
    },
    currentBoardId() {
      if (this.embed) {
        return this.embeddedBoardAttributes.boardId;
      }
      return this.$store.getters.currentBoardId;
    },
    currentBoardProjectId() {
      return this.currentBoard?.project_id;
    },
    currentCardsLayout: {
      get() {
        if (this.embed) {
          return this.cardsLayout;
        }
        return this.$store.getters.cardsLayout(this.currentBoardId);
      },
      set(value) {
        if (this.embed) {
          return;
        }
        this.$store.dispatch('setCardsLayout', { value });
      },
    },
    teamSlug() {
      return this.$store.getters.getTeamSlug;
    },

    teamID() {
      return this.$store.getters.getTeamID;
    },
  },
  watch: {
    currentBoardId(newVal) {
      if (newVal) {
        this.$store.dispatch('setSelectedCardId', '');
        this.tryToSubscribe();
      }
    },
    currentBoardProjectId(newVal, previousProjectId) {
      const boardProjectExpanded = this.$store.getters.getProjectExpanded(
        `${this.teamSlug}-${newVal}`
      );
      if (!newVal && boardProjectExpanded) return;
      this.tryToSubscribe();
      if (!previousProjectId) return;
      const previousBoards = this.$store.getters.getProjectBoards(previousProjectId) || [];
      const previousSprints = Object.values(
        this.$store.getters.getSpaceSprints(previousProjectId)
      ).map((sprint) => sprint.id);
      if (previousBoards.length) {
        this.$socket.updateSubscriptions('boards', [], previousBoards);
      }
      if (previousSprints.length) {
        this.$socket.updateSubscriptions('sprints', [], previousSprints);
      }
    },
    isProjectDeleted(newVal) {
      if (newVal && !this.socketDeletedProject) {
        this.setItemNotFound();
      }
      this.$nextTick(() => {
        this.onPageLoad();
      });
    },
    socketDeletedProject(newVal, oldValue) {
      if (!newVal && oldValue) {
        this.setItemNotFound();
      }
    },
    currentBoard: {
      handler(newValue, oldValue) {
        if (newValue?.id !== oldValue?.id) this.checkCardsLayout();
      },
    },
  },

  mounted() {
    this.tryToSubscribe();
    this.$store.dispatch('setSelectedCardId', '');
    this.setItemNotFound();
  },

  created() {
    if (this.currentBoard.project_id && !this.embed) {
      this.$store.dispatch('setCurrentProjectId', this.currentBoard.project_id);
    }
  },
  unmounted() {
    this.$store.dispatch('displayInfoSidebar', false);
    // if embeded board is destroying and new board is already opened then do not reset the current board id
    if (!this.embed) {
      this.$store.dispatch('setCurrentBoardId', '');
    }
  },
  methods: {
    setItemNotFound() {
      if (this.isProjectDeleted && !this.$store.getters.getItemNotFound) {
        this.$store.dispatch('itemNotFound', true);
      }
    },
    unarchiveBoard() {
      return this.$store
        .dispatch('archiveBoard', { id: this.currentBoard.id, archived: false })
        .then(() => {
          this.$store.dispatch('updateBoardPropertyInBoards', {
            boardId: this.currentBoard.id,
            propertyName: 'archived',
            value: false,
          });
          this.$store.dispatch('addBoardInBoards', this.currentBoard);
          this.$store.dispatch('localAddProjectBoardOrder', {
            projectId: this.currentBoard.project_id,
            boardId: this.currentBoard.id,
          });
          this.$store.dispatch('loadArchived', 'boards');
        });
    },
    subscribeToResource() {
      if (this.currentBoard.id) {
        const isSprintBoard =
          this.currentBoard.id.includes('s') &&
          this.currentBoard.id !== this.$constants.epicsBoardId;
        const resourceType = isSprintBoard ? 'sprints' : 'boards';
        this.$socket.subscribe(resourceType, this.currentBoard.id);
        return;
      }
      this.boardIdWatcher = this.$watch('currentBoard.id', (val) => {
        if (val) {
          this.tryToSubscribe();
          this.boardIdWatcher();
        }
      });
    },
    checkCardsLayout() {
      const boardLayouts = browserStorageGetItem(localBoardLayoutKey(this.teamID));
      const currentBoardLayout = boardLayouts?.[this.currentBoard.id] || '';

      const isTimelineWithoutFeature =
        currentBoardLayout === BoardLayout.Timeline && !this.timelineLayoutFeatureAvailable;
      const isCalendarWithoutFeature =
        currentBoardLayout === BoardLayout.Calendar && !this.calendarLayoutFeatureAvailable;
      if (isTimelineWithoutFeature || isCalendarWithoutFeature) {
        browserStorageGetAndSetItem(
          localBoardLayoutKey(this.teamID),
          this.currentBoardId,
          this.currentBoard.layout
        );
        this.$store.dispatch('setCardsLayout', { value: this.currentBoard.layout });
      }
    },
  },
};
</script>
<style lang="scss">
.board-view-modal-style {
  background: #ffffff;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  @import '@/assets/scss/listOfBoardsWrap.scss';
}
</style>
